// Utilities to build Tradier Trade Link URLs for multileg option orders
import { computeCalendarPrice, computeDiagonalPrice, computeVerticalDebitPrice } from './tos';

const TRADELINK_BASE = 'https://dash.tradier.com/tradelink';

const sanitizeOccSymbol = (symbol) => {
  if (!symbol || typeof symbol !== 'string') return '';
  return symbol.replace(/\s+/g, '');
};

// Extract underlying from OCC option symbol (e.g., SPXW250530C06050000 -> SPX)
export const extractUnderlyingFromOcc = (occSymbol) => {
  const s = sanitizeOccSymbol(occSymbol);
  const firstDigitIdx = s.search(/[0-9]/);
  if (firstDigitIdx <= 0) return '';
  let underlying = s.substring(0, firstDigitIdx);
  // Map weekly suffix like SPXW -> SPX
  if (underlying.endsWith('W')) {
    underlying = underlying.substring(0, underlying.length - 1);
  }
  return underlying;
};

// Build a multileg Trade Link URL. Omits price to let user set limit price manually.
export const buildTradierMultilegLink = ({
  underlying,
  legs, // Array of { option_symbol, side, quantity }
  type, // optional; omit to let ticket default (e.g., Net Debit)
  duration = 'day',
  price, // optional; if provided will be included
}) => {
  if (!underlying || !legs || !Array.isArray(legs) || legs.length === 0) return null;

  const params = new URLSearchParams();
  params.set('class', 'multileg');
  params.set('symbol', underlying);
  if (type) {
    params.set('type', type);
  }
  params.set('duration', duration);
  if (typeof price === 'number' && Number.isFinite(price)) {
    params.set('price', String(price));
  }

  legs.forEach((leg, idx) => {
    const occ = sanitizeOccSymbol(leg.option_symbol);
    if (!occ) return;
    params.set(`option_symbol[${idx}]`, occ);
    if (leg.side) params.set(`side[${idx}]`, leg.side);
    if (leg.quantity !== undefined && leg.quantity !== null) {
      params.set(`quantity[${idx}]`, String(leg.quantity));
    }
  });

  return `${TRADELINK_BASE}?${params.toString()}`;
};

// Builders for the three spread types based on a row object
export const buildCalendarLink = (row) => {
  const spread = row?.calendar_spread;
  const qty = typeof row?.calendar_quantity === 'number' && row.calendar_quantity > 0 ? row.calendar_quantity : 1;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const underlying = extractUnderlyingFromOcc(shortLeg?.symbol || longLeg?.symbol || '');
  if (!underlying) return null;

  const qShort = (shortLeg.ratio ? Math.abs(shortLeg.ratio) : 1) * qty;
  const qLong = (longLeg.ratio ? Math.abs(longLeg.ratio) : 1) * qty;

  const pr = computeCalendarPrice(row);
  const hasPrice = (typeof pr === 'number' && Number.isFinite(pr));
  const isCredit = hasPrice ? pr < 0 : false;
  const price = hasPrice ? Number(Math.abs(pr).toFixed(2)) : undefined;
  return buildTradierMultilegLink({
    underlying,
    legs: [
      { option_symbol: shortLeg?.symbol, side: 'sell_to_open', quantity: qShort },
      { option_symbol: longLeg?.symbol, side: 'buy_to_open', quantity: qLong },
    ],
    type: hasPrice ? (isCredit ? 'credit' : 'debit') : 'debit',
    duration: 'day',
    price,
  });
};

export const buildDiagonalLink = (row) => {
  const spread = row?.diagonal_spread;
  const qty = typeof row?.diagonal_quantity === 'number' && row.diagonal_quantity > 0 ? row.diagonal_quantity : 1;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const underlying = extractUnderlyingFromOcc(shortLeg?.symbol || longLeg?.symbol || '');
  if (!underlying) return null;

  const qShort = (shortLeg.ratio ? Math.abs(shortLeg.ratio) : 1) * qty;
  const qLong = (longLeg.ratio ? Math.abs(longLeg.ratio) : 1) * qty;

  const pr = computeDiagonalPrice(row);
  const hasPrice = (typeof pr === 'number' && Number.isFinite(pr));
  const isCredit = hasPrice ? pr < 0 : false;
  const price = hasPrice ? Number(Math.abs(pr).toFixed(2)) : undefined;
  return buildTradierMultilegLink({
    underlying,
    legs: [
      { option_symbol: shortLeg?.symbol, side: 'sell_to_open', quantity: qShort },
      { option_symbol: longLeg?.symbol, side: 'buy_to_open', quantity: qLong },
    ],
    type: hasPrice ? (isCredit ? 'credit' : 'debit') : 'debit',
    duration: 'day',
    price,
  });
};

export const buildVerticalDebitLink = (row) => {
  const spread = row?.vertical_spread;
  // Fallback to 1 if vertical_quantity is not set, to match other spreads
  const qty = typeof row?.vertical_quantity === 'number' && row.vertical_quantity > 0 ? row.vertical_quantity : 1;
  if (!spread || !Array.isArray(spread.legs) || spread.legs.length < 2) return null;
  // Data convention: [short, long]
  const shortLeg = spread.legs[0];
  const longLeg = spread.legs[1];
  const underlying = extractUnderlyingFromOcc(shortLeg?.symbol || longLeg?.symbol || '');
  if (!underlying) return null;

  const qShort = (shortLeg.ratio ? Math.abs(shortLeg.ratio) : 1) * qty;
  const qLong = (longLeg.ratio ? Math.abs(longLeg.ratio) : 1) * qty;

  const pr = computeVerticalDebitPrice(row);
  const hasPrice = (typeof pr === 'number' && Number.isFinite(pr));
  const isCredit = hasPrice ? pr < 0 : false;
  const price = hasPrice ? Number(Math.abs(pr).toFixed(2)) : undefined;
  return buildTradierMultilegLink({
    underlying,
    legs: [
      { option_symbol: longLeg?.symbol, side: 'buy_to_open', quantity: qLong },
      { option_symbol: shortLeg?.symbol, side: 'sell_to_open', quantity: qShort },
    ],
    type: hasPrice ? (isCredit ? 'credit' : 'debit') : 'debit',
    duration: 'day',
    price,
  });
};


